import React, { useState } from 'react';
import { Button, Tooltip, Typography } from 'antd';
import { ChevronDown, ChevronUp, Eye, EyeOff, Lock, Ungroup, Unlock } from 'lucide-react';
import { Track } from '../../types';
import { KIND_ICONS } from '../../../canvas/constants/object-meta';
import { colors, dimensions, TimelineMode, typography } from '../../../theme';
import { LayerContextMenu } from '../layer-context-menu';

const { Text } = Typography;

type LayerRowProps = {
  track: Track;
  isSelected: boolean;
  isGroup?: boolean;
  mode: TimelineMode;
  onSelect: () => void;
  onToggleVisibility: () => void;
  onToggleLock: () => void;
  onUngroup?: () => void;
  onDuplicate: () => void;
  onDelete: () => void;

  canMoveUp: boolean;
  canMoveDown: boolean;
  onBringForward: () => void;
  onSendBackward: () => void;
};

export const LayerRow: React.FC<LayerRowProps> = ({
  track,
  isSelected,
  isGroup = false,
  mode,
  onSelect,
  onToggleVisibility,
  onToggleLock,
  onUngroup,
  onDuplicate,
  onDelete,
  canMoveUp,
  canMoveDown,
  onBringForward,
  onSendBackward
}) => {
  const td = dimensions.timeline[mode];
  const [hovered, setHovered] = useState(false);
  const showReorder = hovered || isSelected;

  const stop = (fn: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    fn();
  };

  return (
    <LayerContextMenu onDuplicate={onDuplicate} onDelete={onDelete} layerId={track.layerId}>
      <div
        onClick={onSelect}
        onMouseEnter={e => {
          setHovered(true);
          if (!isSelected) e.currentTarget.style.backgroundColor = colors.rowHover;
        }}
        onMouseLeave={e => {
          setHovered(false);
          if (!isSelected) e.currentTarget.style.backgroundColor = colors.rowBg;
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          height: td.rowHeight,
          minHeight: td.rowHeight,
          flexShrink: 0,
          gap: dimensions.spacing.lg,
          padding: `0 ${dimensions.spacing.xl}px`,
          backgroundColor: isSelected ? colors.rowSelected : colors.rowBg,
          borderLeft: `2px solid ${isSelected ? colors.accent : 'transparent'}`,
          cursor: 'pointer',
          userSelect: 'none',
          transition: 'background-color 0.12s, height 0.2s ease, min-height 0.2s ease'
        }}
      >
        <span style={{ color: colors.textSecondary, display: 'flex', flexShrink: 0 }}>
          {React.createElement(KIND_ICONS[track.kind], { size: dimensions.size.iconSm })}
        </span>
        <Text
          ellipsis
          style={{
            flex: 1,
            fontSize: typography.fontSize.base,
            fontFamily: typography.fontFamily.ui,
            color: colors.textPrimary
          }}
        >
          {track.kind}
        </Text>

        <div
          style={{
            display: 'flex',
            gap: dimensions.spacing.xs,
            opacity: showReorder ? 1 : 0,
            pointerEvents: showReorder ? 'auto' : 'none',
            transition: 'opacity 0.12s'
          }}
        >
          {canMoveUp && (
            <Tooltip title='Subir'>
              <Button
                type='text'
                size='small'
                shape='circle'
                disabled={!canMoveUp}
                icon={<ChevronUp size={dimensions.size.iconSm} />}
                onClick={stop(onBringForward)}
                style={{ color: colors.textSecondary }}
              />
            </Tooltip>
          )}
          {canMoveDown && (
            <Tooltip title='Bajar'>
              <Button
                type='text'
                size='small'
                shape='circle'
                disabled={!canMoveDown}
                icon={<ChevronDown size={dimensions.size.iconSm} />}
                onClick={stop(onSendBackward)}
                style={{ color: colors.textSecondary }}
              />
            </Tooltip>
          )}
        </div>

        {isSelected && isGroup && onUngroup && (
          <Tooltip title='Desagrupar (⌘/Ctrl+Shift+G)'>
            <Button
              type='text'
              size='small'
              shape='circle'
              icon={<Ungroup size={dimensions.size.iconSm} />}
              onClick={stop(onUngroup)}
              style={{ color: colors.textSecondary }}
            />
          </Tooltip>
        )}
        <Tooltip title={track.visible ? 'Ocultar' : 'Mostrar'}>
          <Button
            type='text'
            size='small'
            shape='circle'
            icon={track.visible ? <Eye size={dimensions.size.iconSm} /> : <EyeOff size={dimensions.size.iconSm} />}
            onClick={stop(onToggleVisibility)}
            style={{ color: track.visible ? colors.textSecondary : colors.textMuted }}
          />
        </Tooltip>
        <Tooltip title={track.locked ? 'Desbloquear' : 'Bloquear'}>
          <Button
            type='text'
            size='small'
            shape='circle'
            icon={track.locked ? <Lock size={dimensions.size.iconSm} /> : <Unlock size={dimensions.size.iconSm} />}
            onClick={stop(onToggleLock)}
            style={{ color: track.locked ? colors.textMuted : colors.textSecondary }}
          />
        </Tooltip>
      </div>
    </LayerContextMenu>
  );
};
